// Local field validators for the access editors. They gate submit BEFORE a
// request is sent, so the operator gets a specific message instead of a
// generic backend 422. Deliberately lenient where the backend is the real
// authority (IP family/CIDR), strict where the C.3b contract demands it
// (field-qualified vault refs).

export type VendorParse =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string };

export function parseVendorExtras(text: string): VendorParse {
  const t = text.trim();
  if (t === "") return { ok: true, value: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch {
    return { ok: false, error: "Not valid JSON." };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: "Must be a JSON object." };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

// C.3b contract: a resolvable secret ref is vault:<path>#<field>; a bare
// vault:<path> fails materialization with secret_ref_field_required.
export function vaultFieldRefError(v: string): string | null {
  if (v.trim() === "") return null;
  // Validate the ORIGINAL value (do NOT pre-trim): the API persists what was
  // submitted, so surrounding whitespace would pass a trim-then-validate gate yet
  // break the stored ref. Reject it for a clean error, matching the backend.
  if (v !== v.trim()) return "A secret ref must have no leading/trailing whitespace.";
  const t = v;
  if (!t.startsWith("vault:")) return "Must be a vault: reference (no plaintext secrets).";
  // Split on the LAST '#' to match the backend resolver (parse_vault_ref uses
  // rpartition('#')): a '#' inside the path is tolerated; the field is whatever
  // follows the FINAL '#'. So vault:a## has an empty field and is rejected here,
  // exactly as the resolver rejects it.
  const hash = t.lastIndexOf("#");
  if (hash === -1) {
    return "Add #field (e.g. …/setup_key#key) — a bare vault path can't be resolved.";
  }
  const path = t.slice("vault:".length, hash);
  const field = t.slice(hash + 1);
  // Reject empty AND leading/trailing-whitespace path/field — keep at least as
  // strict as the backend DB CHECK so the operator sees a clean error here.
  if (path === "" || field === "" || path !== path.trim() || field !== field.trim()) {
    return "Need vault:<path>#<field> (e.g. …/setup_key#key) — path and field must be non-empty (no surrounding whitespace).";
  }
  return null;
}

// Lenient host-IP guard for an `inet` field: accepts IPv4 dotted-quad or IPv6
// colon-hex, each with an optional /prefix. Never blocks a genuinely valid
// address; anything it misses still surfaces as the backend 422.
export function ipAddressError(v: string): string | null {
  const t = v.trim();
  if (t === "") return null;
  const [addr, prefix, ...rest] = t.split("/");
  if (rest.length > 0) return "Enter a valid IP address (e.g. 10.129.0.1).";
  if (prefix !== undefined && !/^\d{1,3}$/.test(prefix)) {
    return "Enter a valid IP address (e.g. 10.129.0.1).";
  }
  const isV4 =
    /^(\d{1,3})(\.\d{1,3}){3}$/.test(addr) &&
    addr.split(".").every((o) => Number(o) <= 255) &&
    (prefix === undefined || Number(prefix) <= 32);
  // Lenient but not absurd IPv6: hex+colons only, no 3+ consecutive colons,
  // at most one "::" compression group, and at least one hex digit (so bare
  // ":" / "::::" / "fd00:::1" are rejected; "::" and "fd00::1" pass).
  const isV6 =
    addr.includes(":") &&
    /^[0-9a-fA-F:]+$/.test(addr) &&
    !/:::/.test(addr) &&
    (addr.match(/::/g) ?? []).length <= 1 &&
    (/[0-9a-fA-F]/.test(addr) || addr === "::") &&
    (prefix === undefined || Number(prefix) <= 128);
  return isV4 || isV6 ? null : "Enter a valid IP address (e.g. 10.129.0.1).";
}
